import cx from 'classnames';
import {
    SEO_DESCRIPTION_DEFAULT_FALLBACK,
    SEO_TITLE_DEFAULT_FALLBACK,
    SEO_TITLE_HOMEPAGE,
    SEO_TITLE_title_TEMPLATE_WITH_BRANDING,
} from 'mk/autogenerated/translations/Page.ccb16de2a94c61087a5f6623458cc1d0'
import { ACTIVE_SERVER_ID } from 'mk/settings';
import AdminclubHeader from 'mk2/apps/adminclub/components/AdminclubHeader';
import BoTabs from 'mk2/apps/bo/components/BoTabs';
import { loginUrl } from 'mk2/apps/users/urls';
import { Footer } from 'mk2/components/Footer';
import Header from 'mk2/components/Header';
import ScrollToTop from 'mk2/components/ScrollToTop';
import { AdSlotBranding } from 'mk2/containers/AdSlotBranding/AdSlotBranding';
import AdSlotInterstitial from 'mk2/containers/AdSlotInterstitial/AdSlotInterstitial';
import { AdSlotSticky } from 'mk2/containers/AdSlotSticky/AdSlotSticky';
import { AppName, Route } from 'mk2/containers/Application/Application';
import PhoneVerificationModal from 'mk2/containers/PhoneVerificationModal/PhoneVerificationModal';
import SiteMenu from 'mk2/containers/SiteMenu/SiteMenu';
import { hasRequiredPermissions } from 'mk2/helpers/permissions';
import { organizationModrykonik } from 'mk2/helpers/structuredData';
import { url } from 'mk2/helpers/urls';
import PageDefinition, { BrandingProps, HalfpageProps, PageLayout, RightContentSide } from 'mk2/pages/Page';
import { isFailure, LoadingState } from 'mk2/reducers/loadingState';
import { UserEntity } from 'mk2/schemas';
import { redirectInPWA } from 'mk2/services/browserHistory';
import { interpolate } from 'mk2/services/i18n';
import React from 'react';
import Helmet from 'react-helmet';
import ReduxToastr from 'react-redux-toastr';
import { RouteComponentProps } from 'react-router';
import { StickyContainer } from 'react-sticky';
import styles from './Page.mscss';

interface ChildrenProps {
    app: AppName;
    content: (JSX.Element | null | false);
    rightContent: (JSX.Element | null | false);
    halfpage: (JSX.Element | null | false);
}

interface OwnProps {
    app: AppName;
    baseUrl: string;
    isMobile: boolean;
    loadingState?: LoadingState;  // CeMi: posiela sa toto odniekal vobec?
    requestUser: UserEntity;
    requestUserPermissions: string[];
    route: Route;
    pageInstance: PageDefinition<any, any, any>;
}

interface OwnState {
    hasPermission: boolean;
}

type RouteProps = RouteComponentProps<any>;

export type Props = OwnProps & RouteProps;

class Page extends React.PureComponent<Props, OwnState> {

    public static whyDidYouRender = true;

    public static getDerivedStateFromProps(props: Props, state: OwnState) {
        const { requestUserPermissions, pageInstance } = props;

        const permissions: string | string[] | boolean = pageInstance.getPermissionRequired(props);
        const hasPermission = hasRequiredPermissions(requestUserPermissions, permissions);

        return state.hasPermission !== hasPermission
            ? { hasPermission }
            : null;  // nothing tu update
    }

    constructor(props: Props) {
        super(props);

        this.state = {
            hasPermission: true,
        };
    }

    public componentDidMount() {
        this.redirectIfMissingPermission();

    }
    public componentDidUpdate(prevProps: Props) {
        this.redirectIfMissingPermission();
    }

    public render() {
        const { pageInstance, app, ...rest } = this.props;
        const { baseUrl, location, history } = this.props;

        if (!this.state.hasPermission) {
            if (typeof window === 'undefined') {
                // CeMi: zatial neviem o ziadnom inom rozumnom sposobe ako pustat side-effecty pre SSR
                // (ked sa nezavola componentDidMount alebo componentDidUpdate)
                this.redirectIfMissingPermission();
            }

            // do not render inner content, until browser handles the url redirect
            return null;
        }

        // If loadingState is failure - disable ads
        const failureLoadingState = isFailure(rest.loadingState);

        const adKey = hashCode(`${location.pathname}${location.search.replace(/[?&]?(q|search)=[^&]+/, '')}`);

        const brandingProps: BrandingProps = !rest.isMobile && !failureLoadingState && ACTIVE_SERVER_ID !== 207
            ? pageInstance.getBrandingProps(rest)
            : null;
        const branding = brandingProps
            ? <AdSlotBranding zone={brandingProps.zone} targeting={brandingProps.targeting} />
            : null;

        const title = pageInstance.getMetaTitle(rest);
        const description = pageInstance.getMetaDescription(rest);
        const currentUrl = `${location.pathname}${location.search}`;
        const opengraphData = pageInstance.getOpenGraphData(rest, baseUrl, currentUrl);
        const structuredData = pageInstance.getStructuredData(rest);
        const canonicalUrl = pageInstance.getCanonicalUrl(baseUrl, rest);

        const headerTitle = pageInstance.getHeaderTitle(rest);
        const headerLeft = pageInstance.getHeaderLeft(rest);
        const headerRight = pageInstance.getHeaderRight(rest);
        const tabs = pageInstance.getHeaderTabs(rest);

        const pageHeader = pageInstance.hasPageHeaderPart1(rest)
            ? (app !== 'bo' ? (
                <React.Fragment>
                    <SiteMenu
                        menuSelection={pageInstance.getHeaderMenuSelection(app, rest)}
                        fluxibleCompatibility={false}
                        currentUrl={currentUrl}
                        isHeaderSticky={pageInstance.isHeaderSticky(rest)}
                        headerHasBackBtn={pageInstance.hasHeaderBackButton(rest)}
                        headerTitle={headerTitle}
                        headerLeft={headerLeft}
                        headerRight={headerRight}
                        headerTabs={tabs}
                        mobileShowLogo={pageInstance.hasMobileLogo(rest)}
                        mergedForumAndGroups={pageInstance.hasMergedForumAndGroups(rest)}
                    />
                    {app === 'adminclub' && (
                        <AdminclubHeader
                            menuSelection={pageInstance.getHeaderMenuSelection(app, rest)}
                            fluxibleCompatibility={false}
                        />
                    )}
                </React.Fragment>
            ) : (
                <BoTabs
                    menuSelection={pageInstance.getHeaderMenuSelection(app, rest)}
                    fluxibleCompatibility={false}
                />
            ))
            : (// Page has no header - but we still need toastr container
                <ReduxToastr
                    position="top-center"
                    transitionIn="fadeIn"
                    transitionOut="fadeOut"
                />
            );

        // na desktop je cast headeru (back a action button) priamo v content stranky v hlavnom stlpci
        const contentHeader = !rest.isMobile && (headerTitle || headerLeft || headerRight || (tabs && tabs.length))
            ? (
                <div className={styles.Page__contentHeader}>
                    <Header
                        heading={headerTitle}
                        left={headerLeft}
                        right={headerRight}
                        showLogo={ACTIVE_SERVER_ID === 207 && pageInstance.hasMobileLogo(rest)}
                    />

                    {tabs && tabs.length && (
                        <div className={styles.Page__contentHeaderTabs}>
                            {tabs.map((tab, index) => tab && (
                                <div key={index} className={styles.Page__contentHeaderTab}>{tab}</div>
                            ))}
                        </div>
                    )}
                </div>
            )
            : null;

        const contentFooter = pageInstance.getFooter(rest);

        const content = (
            <React.Fragment>
                {contentHeader}

                {pageInstance.render(rest)}

                {contentFooter && (
                    <div className={styles.Page__contentFooter}>
                        {contentFooter}
                    </div>
                )}
            </React.Fragment>
        );

        const pageLayout: PageLayout = pageInstance.getPageLayout(app, rest);
        let rightContent = null;
        if (pageLayout === PageLayout.MainContentWithRightColumn) {
            const renderRight = pageInstance.renderRight(rest);
            const halfpageProps: HalfpageProps = !rest.isMobile && !failureLoadingState
                ? pageInstance.getHalfpageProps(rest)
                : null;

            if (renderRight || halfpageProps) {
                rightContent = (
                    <React.Fragment>
                        {renderRight}
                        {!!halfpageProps && (
                            <div className={cx(styles.Page__ad, styles[`Page__ad--${app}`])}>
                                <AdSlotSticky
                                    key={adKey}
                                    zone={halfpageProps.zone}
                                    targeting={halfpageProps.targeting}
                                    requestUser={rest.requestUser}
                                />
                            </div>
                        )}
                    </React.Fragment>
                );
            }
        }

        const rightContentSide = pageInstance.getRightContentSide(app, rest) === RightContentSide.RightSide ? 'right' : 'left';

        return (
            <React.Fragment> {/* <div className={styles.Page}> reduce DOM depth */}
                <Helmet>
                    <title>{title
                        // kvoli seo chceme, aby kazda stranka mala vzdy <title> a aby na konci title bolo
                        // '- Modry konik'. Google vie sam pridat '- Modry konik', ale pre Seznam to musime
                        // explicitne pridat my
                        ? (title !== SEO_TITLE_HOMEPAGE
                            ? interpolate(SEO_TITLE_title_TEMPLATE_WITH_BRANDING, {title})
                            : title // homepage uz ma 'Modry konik -' na zaciatku
                        ) : SEO_TITLE_DEFAULT_FALLBACK
                    }</title>
                    <meta name="description" content={description || SEO_DESCRIPTION_DEFAULT_FALLBACK} />
                    <meta name="robots" content={pageInstance.getMetaRobots(rest)} />
                    {canonicalUrl && (
                        <link rel="canonical" href={canonicalUrl} />
                    )}
                    {opengraphData && Object.keys(opengraphData).map((ogKey: string) => {
                        const ogContent = opengraphData[ogKey];
                        return Array.isArray(ogContent) ? (
                            ogContent.map((cnt, index) => (
                                <meta key={`${ogKey}:${index}`} property={ogKey} content={cnt} />
                            ))
                        ) : (
                            <meta key={ogKey} property={ogKey} content={ogContent} />
                        );
                    })}
                    <script type="application/ld+json">
                        {JSON.stringify(organizationModrykonik())}
                    </script>
                    {structuredData && structuredData.map((sd, index) => (
                        <script key={`sdata-${index}`} type="application/ld+json">
                            {JSON.stringify(sd)}
                        </script>
                    ))}
                </Helmet>

                <AdSlotInterstitial
                    zone={rest.isMobile ? 'Interstitial (Mobile)' : 'Interstitial (Desktop)'}
                    route={this.props.route}
                    location={this.props.location}
                    requestUser={rest.requestUser}
                    requestUserPermissions={rest.requestUserPermissions}
                >
                    {branding}

                    <StickyContainer id="page-wrap" className={styles.Page__wrap}>
                        <ScrollToTop location={location} history={history}>
                            <React.Fragment>
                                {pageHeader}

                                <div
                                    className={cx(
                                        styles.Page__content,
                                        styles[`Page__content--${PageLayout[pageLayout]}`],
                                        styles[`Page__content--${app}`],
                                    )}
                                >
                                    <div
                                        className={cx(
                                            styles.Page__mainContent,
                                            styles[`Page__mainContent--${PageLayout[pageLayout]}`],
                                            styles[`Page__mainContent--${app}`],
                                            styles[`Page__mainContent--${rightContentSide}`],
                                        )}
                                    >
                                        {content}
                                    </div>
                                    {rightContent && (
                                        <div className={cx(styles.Page__rightContent, styles[`Page__rightContent--${app}`])}>
                                            {rightContent}
                                        </div>
                                    )}
                                </div>

                                {!rest.isMobile && pageInstance.hasPageFooter(app, rest) && <Footer />}
                            </React.Fragment>
                        </ScrollToTop>
                    </StickyContainer>

                    <PhoneVerificationModal />
                </AdSlotInterstitial>
            </React.Fragment>
        );
    }

    private redirectIfMissingPermission() {
        if (!this.state.hasPermission) {
            const { history, location } = this.props;
            const gotoUrl = url(loginUrl, {}, {next: `${location.pathname}${location.search}`});
            redirectInPWA(history, gotoUrl, 401);
        }
    }
}

export default Page;

function hashCode(value) {
    let hash = 0;
    if (value.length === 0) {
        return hash;
    }

    for (let i = 0; i < value.length; i++) {
        const char = value.charCodeAt(i);
        hash = ((hash << 5) - hash) + char; // tslint:disable-line no-bitwise
        // Convert to 32bit integer
        hash = hash & hash; // tslint:disable-line no-bitwise
    }

    return hash;
}
